import React from 'react';
import { Star, Quote, ExternalLink, Play } from 'lucide-react';
import { Button } from './ui/button';
import { testimonials, businessInfo } from '../data/mock';

const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-sage-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-warm-100/30 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sage-600 font-medium tracking-wider uppercase text-sm mb-4">
            Client Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-6">
            What Our Clients
            <span className="block text-sage-600">Say About Us</span>
          </h2>
          <p className="text-stone-600 text-lg">
            Real experiences from our valued clients who have found relief, 
            relaxation, and renewal through our massage therapy.
          </p>
          
          {/* TripAdvisor Badge */}
          <div className="mt-6 inline-flex items-center gap-3 bg-white shadow-lg rounded-full px-6 py-3">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <div key={`tripadvisor-${i}`} className="w-4 h-4 bg-green-500 rounded-full" />
              ))}
            </div>
            <span className="text-stone-700 font-medium">Excellent Reviews on TripAdvisor</span>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="bg-gradient-to-br from-stone-50 to-sage-50/30 rounded-3xl p-8 relative group hover:shadow-xl transition-all duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 text-sage-200">
                <Quote size={48} />
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`rating-${i}`} size={20} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {testimonial.source && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    {testimonial.source}
                  </span>
                )}
              </div>

              {/* Content */}
              <p className="text-stone-700 text-lg leading-relaxed mb-6 relative z-10">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-stone-800">{testimonial.name}</p>
                  <p className="text-sm text-sage-600">{testimonial.service}</p>
                </div>
                <span className="text-sm text-stone-400">{testimonial.date}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Video Testimonials Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-semibold text-stone-800 text-center mb-8">
            Video Testimonials & Our Space
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* TikTok Testimonial */}
            <a
              href={businessInfo.tiktokTestimonialVideo}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="aspect-[9/16] max-h-[400px] relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
                <div className="relative z-10 text-center p-6">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={36} className="text-white ml-1" />
                  </div>
                  <p className="text-white font-semibold text-lg">Watch Client Testimonial</p>
                  <p className="text-white/70 text-sm mt-2">on TikTok</p>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  <span className="text-white text-sm">@dublin.holistic.m</span>
                </div>
              </div>
            </a>

            {/* Instagram Studio Tour */}
            <a
              href={businessInfo.instagramStudioVideo}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="aspect-[9/16] max-h-[400px] relative flex items-center justify-center">
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative z-10 text-center p-6">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={36} className="text-white ml-1" />
                  </div>
                  <p className="text-white font-semibold text-lg">Tour Our Studio</p>
                  <p className="text-white/80 text-sm mt-2">on Instagram</p>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="text-white text-sm">@dublin.holistic.m</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
          {/* TripAdvisor Link */}
          <a
            href={businessInfo.tripadvisorUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white shadow-lg rounded-2xl px-6 py-4 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-stone-800 group-hover:text-green-600 transition-colors">Read More Reviews</p>
              <p className="text-sm text-stone-500">on TripAdvisor</p>
            </div>
            <ExternalLink size={18} className="text-stone-400 group-hover:text-green-600 transition-colors" />
          </a>

          {/* TikTok Link */}
          <a
            href={businessInfo.tiktokUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white shadow-lg rounded-2xl px-6 py-4 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-stone-900 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-stone-800 group-hover:text-stone-600 transition-colors">Follow Us</p>
              <p className="text-sm text-stone-500">@dublin.holistic.m</p>
            </div>
            <ExternalLink size={18} className="text-stone-400 group-hover:text-stone-600 transition-colors" />
          </a>

          {/* Instagram Link */}
          <a
            href={businessInfo.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white shadow-lg rounded-2xl px-6 py-4 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-stone-800 group-hover:text-pink-600 transition-colors">Follow Us</p>
              <p className="text-sm text-stone-500">@dublin.holistic.m</p>
            </div>
            <ExternalLink size={18} className="text-stone-400 group-hover:text-pink-600 transition-colors" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
